/*  Proposta C — Manutenzioni Programmate
    Calendar mensile dark + lista lato. Stress test del dark mode su layout calendario.
*/

import React from 'react';
import { useV2Fonts } from './_shared';
import { CShell, C, CBadge, CCard } from './_cShell';
import { ChevronLeft, ChevronRight, Calendar, Plus, CheckCircle2, Clock } from 'lucide-react';

// Mock: ogni giorno ha eventi (hotel + tipo)
const HOTELS_PALETTE = {
  GAL: '#3B82F6', // blue
  ARC: '#A855F7', // purple
  MNZ: '#F59E0B', // amber
  KIR: '#10B981', // green
};

// Genera eventi pseudo-random deterministici per il mese
const buildMonth = () => {
  const days = [];
  for (let d = 1; d <= 30; d++) {
    const seed = (d * 17 + 5) % 100;
    const events = [];
    if (seed < 35) {
      const hotels = Object.keys(HOTELS_PALETTE);
      const h = hotels[d % hotels.length];
      events.push({ hotel: h, count: (seed % 3) + 1 });
    }
    if (seed > 60 && seed < 85) {
      events.push({ hotel: 'ARC', count: 1 });
    }
    if (seed > 90) {
      events.push({ hotel: 'KIR', count: 2 });
    }
    days.push({ day: d, events });
  }
  return days;
};

const UPCOMING = [
  { date: '14 GIU', hotel: 'GAL', title: 'Controllo impianto elettrico semestrale', area: 'Quadri elettrici p.terra', tech: 'M. Rossi', status: 'pending' },
  { date: '14 GIU', hotel: 'GAL', title: 'Sanificazione filtri VRV piano 4', area: '405–420', tech: 'D. Conti', status: 'pending' },
  { date: '15 GIU', hotel: 'ARC', title: 'Verifica ascensore B (cadenza trimestrale)', area: 'Asc-B', tech: 'Ext. Otis', status: 'scheduled' },
  { date: '15 GIU', hotel: 'KIR', title: 'Manutenzione caldaie all-electric', area: 'Centrale', tech: 'A. Costa', status: 'pending' },
  { date: '16 GIU', hotel: 'MNZ', title: 'Pulizia pannelli solari', area: 'Tetto', tech: 'M. Rossi', status: 'pending' },
  { date: '17 GIU', hotel: 'GAL', title: 'Estintori — controllo carica', area: 'Tutto edificio', tech: 'Ext. Aspire', status: 'scheduled' },
];

const RECENT_DONE = [
  { date: '11 GIU', hotel: 'GAL', title: 'Sanificazione filtri VRV piano 3', tech: 'D. Conti' },
  { date: '10 GIU', hotel: 'ARC', title: 'Ispezione antincendio piano interrato', tech: 'Ext. Aspire' },
  { date: '08 GIU', hotel: 'KIR', title: 'Trattamento acqua piscina', tech: 'A. Costa' },
];

const DAY_NAMES = ['LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM'];

const ProposalCScheduled = () => {
  useV2Fonts();
  const month = buildMonth();
  // 1 giugno cade di domenica nel mock — offset 6
  const firstDayOffset = 6;
  const cells = [];
  for (let i = 0; i < firstDayOffset; i++) cells.push(null);
  cells.push(...month);
  while (cells.length % 7 !== 0) cells.push(null);

  const today = 14;

  return (
    <CShell hotel="Multi-Hotel View">
      {/* Header */}
      <div className="mb-5 flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
            MODULE_05 · SCHEDULED_MAINT
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: C.fontDisplay }}>Manutenzioni Programmate</h1>
          <p className="text-xs mt-1" style={{ color: C.muted, fontFamily: C.fontMono }}>
            Giugno 2026 · 18 scheduled · 4 today · 0 overdue
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="px-3 py-1.5 text-xs rounded-md flex items-center gap-1.5"
            style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.text, fontFamily: C.fontMono }}
          >
            <Calendar className="w-3.5 h-3.5" /> MONTH
          </button>
          <button
            className="px-3 py-1.5 text-xs rounded-md flex items-center gap-1.5"
            style={{
              background: C.accent,
              color: '#fff',
              fontFamily: C.fontMono,
              boxShadow: `0 0 18px ${C.accentGlow}`,
            }}
          >
            <Plus className="w-3.5 h-3.5" /> NEW_PROGRAM
          </button>
        </div>
      </div>

      {/* Hotel legend */}
      <div className="flex items-center gap-4 mb-4 text-[10px]" style={{ color: C.muted, fontFamily: C.fontMono }}>
        <span>LEGEND:</span>
        {Object.entries(HOTELS_PALETTE).map(([h, color]) => (
          <span key={h} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
            {h}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Calendar — 8 cols */}
        <div className="col-span-8 rounded-md overflow-hidden" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
            <button className="p-1 rounded" style={{ background: C.surface2 }}>
              <ChevronLeft className="w-4 h-4" style={{ color: C.muted }} />
            </button>
            <div className="text-sm font-semibold tabular-nums" style={{ fontFamily: C.fontDisplay }}>
              GIUGNO 2026
            </div>
            <button className="p-1 rounded" style={{ background: C.surface2 }}>
              <ChevronRight className="w-4 h-4" style={{ color: C.muted }} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7" style={{ borderBottom: `1px solid ${C.border}` }}>
            {DAY_NAMES.map((d) => (
              <div
                key={d}
                className="px-3 py-2 text-[10px] tracking-widest text-center"
                style={{ color: C.mutedDim, fontFamily: C.fontMono, background: C.surface2 }}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Cells */}
          <div className="grid grid-cols-7" style={{ gridAutoRows: '85px' }}>
            {cells.map((cell, i) => {
              const isToday = cell && cell.day === today;
              const isWeekend = i % 7 === 5 || i % 7 === 6;
              return (
                <div
                  key={i}
                  className="border-r border-b p-2 relative"
                  style={{
                    borderColor: C.border,
                    background: !cell ? C.bg : isToday ? 'rgba(59,130,246,0.07)' : C.surface,
                  }}
                >
                  {cell && (
                    <>
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-xs tabular-nums ${isToday ? 'font-bold' : ''}`}
                          style={{
                            color: isToday ? C.accent : (isWeekend ? C.mutedDim : C.muted),
                            fontFamily: C.fontMono,
                          }}
                        >
                          {String(cell.day).padStart(2, '0')}
                        </span>
                        {isToday && (
                          <span
                            className="text-[8px] px-1 rounded font-semibold tracking-widest"
                            style={{ background: C.accent, color: '#fff', fontFamily: C.fontMono }}
                          >
                            TODAY
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 space-y-0.5">
                        {cell.events.map((ev, ei) => (
                          <div
                            key={ei}
                            className="text-[9px] px-1 py-0.5 rounded truncate"
                            style={{
                              background: `${HOTELS_PALETTE[ev.hotel]}1f`,
                              color: HOTELS_PALETTE[ev.hotel],
                              fontFamily: C.fontMono,
                              borderLeft: `2px solid ${HOTELS_PALETTE[ev.hotel]}`,
                            }}
                          >
                            {ev.hotel} · {ev.count}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right column — 4 cols */}
        <div className="col-span-4 space-y-4">
          <CCard label="UPCOMING_07" title="Prossime 6 da affrontare" padding="p-4">
            <div className="space-y-2.5">
              {UPCOMING.map((u, i) => (
                <div key={i} className="flex items-start gap-2.5 pb-2.5" style={{ borderBottom: i < UPCOMING.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <div className="text-center min-w-[40px]">
                    <div className="text-[10px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>{u.date.split(' ')[1]}</div>
                    <div className="text-base font-bold tabular-nums" style={{ fontFamily: C.fontDisplay, color: HOTELS_PALETTE[u.hotel] }}>
                      {u.date.split(' ')[0]}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <CBadge color={HOTELS_PALETTE[u.hotel]}>{u.hotel}</CBadge>
                      {u.status === 'scheduled' && <CBadge color={C.accent}>EXT</CBadge>}
                    </div>
                    <div className="text-xs font-medium leading-tight">{u.title}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: C.muted, fontFamily: C.fontMono }}>
                      {u.area} · {u.tech}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CCard>

          <CCard label="HISTORY_08" title="Completate di recente" padding="p-4">
            <div className="space-y-2">
              {RECENT_DONE.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5" style={{ color: C.success }} />
                  <span className="flex-1 truncate" style={{ color: C.muted }}>{r.title}</span>
                  <span style={{ color: C.mutedDim, fontFamily: C.fontMono }} className="text-[10px]">
                    {r.date} · {r.hotel}
                  </span>
                </div>
              ))}
            </div>
          </CCard>
        </div>
      </div>
    </CShell>
  );
};

export default ProposalCScheduled;
